import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';

export default function DragDropImageUpload({ 
  onFileSelect, 
  type = 'image', 
  uploading, 
  imageUrl, 
  label = "Click to select and crop image" 
}) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Configuration for limits and types
  const isVideo = type === 'video';
  const accepts = isVideo ? 'video/mp4' : 'image/jpeg, image/png, image/webp';
  const maxSize = isVideo ? 200 * 1024 * 1024 : 5 * 1024 * 1024;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (uploading) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleClick = () => {
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
      e.target.value = null; // reset input
    }
  };

  const processFile = (file) => {
    // Format Validation
    if (isVideo) {
      if (file.type !== 'video/mp4') {
        return toast.error('Only MP4 videos are supported.');
      }
    } else {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        return toast.error('Only JPG, PNG, and WEBP formats are allowed.');
      }
    }

    // Size Validation
    if (file.size > maxSize) {
      const sizeMB = maxSize / (1024 * 1024);
      return toast.error(`File size exceeds the limit of ${sizeMB}MB.`);
    }

    // Pass validated file to parent
    onFileSelect(file, type);
  };

  return (
    <div 
      className={`upload-area ${isDragging ? 'upload-area-drag' : ''}`} 
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      style={{ 
        borderColor: isDragging ? 'var(--primary)' : undefined,
        background: isDragging ? 'var(--primary-light)' : undefined,
        color: isDragging ? 'var(--primary)' : undefined
      }}
    >
      <input 
        type="file" 
        ref={fileInputRef} 
        style={{ display: 'none' }} 
        accept={accepts} 
        onChange={handleFileChange} 
      />

      {imageUrl ? (
        <img src={imageUrl} alt="preview" className="upload-preview" />
      ) : (
        <div style={{ pointerEvents: 'none' }}>
          {uploading ? (
            <p>Processing...</p>
          ) : (
            <>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
              <p style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: 'var(--text)' }}>
                Drag & Drop Image Here
              </p>
              <p style={{ margin: '8px 0', fontSize: '14px' }}>or</p>
              <p style={{ fontWeight: 'bold', fontSize: '15px', color: 'var(--primary)', marginBottom: '16px' }}>
                Click to Browse
              </p>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                Supported formats:<br/>JPG, PNG, WEBP
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
